import React, { useState, useEffect, useCallback } from 'react';
import { X, PlusSquare, AlertTriangle, CheckCircle2, Loader2, RefreshCw } from 'lucide-react';
import { stcBinService } from '../../../services/stcBinService';
import { ExtendShelfResponse } from '../../../types/stcBin';
import { useTranslation } from '../../../contexts/I18nProvider';

export interface ExtendShelfModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Kalles etter vellykket opprettelse med den nye BinCode-en */
  onCreated?: (newBinCode: string) => void;
  /**
   * Foreslått parent-BinCode (f.eks. aktiv linjes til-lokasjon).
   * Operatør kan redigere feltet før forhåndsvisning hentes.
   */
  initialParentBinCode?: string;
}

/**
 * Modal for å utvide en eksisterende hylle med neste lokasjon i sekvensen.
 *
 * Flyt:
 *   1. Operatør skriver/bekrefter parent-BinCode
 *   2. Systemet henter preview (neste BinCode + arveliste)
 *   3. Operatør bekrefter → POST til extend-shelf
 *   4. onCreated() callback trigges, modal lukkes
 */
export const ExtendShelfModal: React.FC<ExtendShelfModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  initialParentBinCode = '',
}) => {
  const { t } = useTranslation();

  const [parentBinCode, setParentBinCode] = useState(initialParentBinCode);
  const [preview, setPreview] = useState<ExtendShelfResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Reset når modal åpnes ──
  useEffect(() => {
    if (isOpen) {
      setParentBinCode(initialParentBinCode);
      setPreview(null);
      setError(null);
      setLoading(false);
      setCreating(false);
    }
  }, [isOpen, initialParentBinCode]);

  // ── ESC for å lukke ──
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !creating) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, creating]);

  // ── Hent preview ──
  const fetchPreview = useCallback(async (binCode: string) => {
    if (!binCode.trim()) {
      setPreview(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await stcBinService.previewExtendShelf(binCode.trim());
      setPreview(response);
    } catch (err) {
      console.error('Failed to preview extend-shelf:', err);
      setError(err instanceof Error ? err.message : 'Ukjent feil ved forhåndsvisning');
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Auto-preview når initialParentBinCode er satt ved åpning ──
  useEffect(() => {
    if (isOpen && initialParentBinCode) {
      fetchPreview(initialParentBinCode);
    }
  }, [isOpen, initialParentBinCode, fetchPreview]);

  // ── Opprett ──
  const handleCreate = async () => {
    if (!preview?.success || !preview.parentBinCode) return;

    setCreating(true);
    setError(null);
    try {
      const response = await stcBinService.extendShelf(preview.parentBinCode);
      if (response.success && response.newBinCode) {
        onCreated?.(response.newBinCode);
        onClose();
      } else {
        // Preview var OK, men opprettelse feilet (f.eks. race condition)
        setPreview(response);
        setError(response.errorMessage || 'Opprettelse feilet');
      }
    } catch (err) {
      console.error('Failed to extend shelf:', err);
      setError(err instanceof Error ? err.message : 'Ukjent feil ved opprettelse');
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  const canCreate = preview?.success === true && !creating;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !creating) onClose();
      }}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <PlusSquare className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              {t('warehouse.extendShelf.title')}
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={creating}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 overflow-y-auto flex-1">
          {/* Parent input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('warehouse.extendShelf.parentLabel')}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={parentBinCode}
                onChange={(e) => setParentBinCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    fetchPreview(parentBinCode);
                  }
                }}
                placeholder={t('warehouse.extendShelf.parentPlaceholder')}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono uppercase"
                disabled={creating}
                autoFocus
              />
              <button
                type="button"
                onClick={() => fetchPreview(parentBinCode)}
                disabled={loading || creating || !parentBinCode.trim()}
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 rounded-md text-sm font-medium text-gray-700 flex items-center gap-1"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                {t('warehouse.extendShelf.preview')}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {t('warehouse.extendShelf.parentHint')}
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Preview: feiltilfeller med errorCode */}
          {preview && !preview.success && preview.errorCode && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm text-amber-800">
                <p className="font-medium">
                  {t(`warehouse.extendShelf.errors.${preview.errorCode}`)}
                </p>
                {preview.errorMessage && (
                  <p className="mt-1 text-amber-700">{preview.errorMessage}</p>
                )}
                {preview.errorCode === 'SHELF_FULL' && preview.stopValue && (
                  <p className="mt-2 text-xs text-amber-600">
                    {t('warehouse.extendShelf.stopValueNote', { stopValue: preview.stopValue })}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Preview: suksess */}
          {preview?.success && (
            <>
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">
                    {t('warehouse.extendShelf.proposalTitle')}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">
                      {t('warehouse.extendShelf.parentLabel')}
                    </div>
                    <div className="font-mono font-semibold text-gray-900 mt-0.5">
                      {preview.parentBinCode}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">
                      {t('warehouse.extendShelf.newBinCode')}
                    </div>
                    <div className="font-mono font-bold text-blue-700 text-lg mt-0.5">
                      {preview.newBinCode}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">
                      {t('warehouse.extendShelf.shelfPrefix')}
                    </div>
                    <div className="font-mono text-gray-700 mt-0.5">
                      {preview.shelfPrefix}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">
                      {t('warehouse.extendShelf.currentMax')}
                    </div>
                    <div className="text-gray-700 mt-0.5">
                      {preview.currentMaxIndex ?? 0} → <strong>{preview.nextIndex}</strong>
                      {preview.lastElementName && (
                        <span className="ml-1 text-xs text-gray-500">
                          ({preview.lastElementName})
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Arveliste */}
              <details className="mb-4">
                <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                  {t('warehouse.extendShelf.inheritedTitle', {
                    count: preview.inheritedFields.length
                  })}
                </summary>
                <div className="mt-2 p-3 bg-gray-50 rounded-md">
                  <div className="flex flex-wrap gap-1">
                    {preview.inheritedFields.map((field) => (
                      <span
                        key={field}
                        className="inline-block px-2 py-0.5 bg-white border border-gray-200 rounded text-xs font-mono text-gray-700"
                      >
                        {field}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {t('warehouse.extendShelf.notInheritedNote')}
                  </p>
                </div>
              </details>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={creating}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            {t('common.buttons.cancel')}
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={!canCreate}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {creating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('warehouse.extendShelf.creating')}
              </>
            ) : (
              <>
                <PlusSquare className="w-4 h-4" />
                {t('warehouse.extendShelf.create')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
